// Story 8.1 (ADO-2010), Task 5.1: the ordering-choices read.
//
// `GET /api/v1/ordering/choices` (order-capture `OrderingChoicesController`, JwtAuthGuard)
// returns where the caller may order: the ordering entries (one per depot, plus the
// WOW / Fruit & Veg / Corrib Retail / LSN ranges) with the Trading Accounts at each, and
// the caller's open DRAFT carts. Hand-duplicated from the backend
// `OrderingChoicesDto` (`libs/modules/order-capture/src/contracts/dtos/ordering-choices.dto.ts`)
// until the generated client lands; keep the two in lock-step.
//
// Unlike the session reads, this one THROWS on failure: "no accounts to order against"
// (`linked: false`, a 200) and "the lookup failed" are different states (A2.2), and the
// choice screen shows a retry for the second. Malformed rows are dropped rather than
// failing the whole read; a malformed optional field (`heldAccountCount`, an open cart's
// `value`) is left out of its row instead, and a missing or malformed open cart account reads
// as none. Every value here is a routing / display hint
// only: the server re-checks the depot on every basket call.
//
// Enabler E8-G (ADO-3109): each entry says whether it shows as a tile of its own
// (`showsAsOwnTile`, task 2.6), and each open cart names the entry kind it belongs to (DA-8,
// AC9: Chill and Fruit & Veg at depot 25 are two carts). DA-10 (AC11): a staff user picks the
// store account it orders for from `GET /api/v1/ordering/accounts` (`searchStaffAccounts`).

import { isMoneyValue, type MoneyValue } from '../shared/money';
import { API_PREFIX, apiFetch } from './shoplink-api';

export const ORDERING_CHOICES_PATH = `${API_PREFIX}/ordering/choices`;

/** `TRADING_ACCOUNTS`: a store user (entries from the Customer). `DEPOT_IDS`: staff. */
export type OrderingScope = 'TRADING_ACCOUNTS' | 'DEPOT_IDS';
export type OrderingEntryKind = 'DEPOT' | 'WOW' | 'FRUIT_AND_VEG' | 'CORRIB_RETAIL' | 'LSN';
/** Per-Trading-Account type (not the per-tenant `accountType`); drives the landing page. */
export type TradingAccountType = 'LSN' | 'LICENSED_TRADE' | 'FOODSERVICE' | 'DEFAULT';

export interface OrderingAccount {
  /** The "Account Number" (SL1.0 `depot_customer_code`). */
  readonly customerAccountCode: string;
  /** The "Account Name" (SL1.0 `cname`); '' when blank. */
  readonly accountName: string;
  readonly tradingAccountType: TradingAccountType;
  /** Story 11.10 (ADO-1961), D-3: whether the store sees its retail margin when ordering as this
   *  account at this entry, known before any price is read. Absent or malformed reads `false`. */
  readonly marginShown: boolean;
}

export interface OrderingEntry {
  /** `${kind}:${depotCode}`: unique in a response and stable across reads. */
  readonly key: string;
  readonly kind: OrderingEntryKind;
  /** The app depot UUID every basket call for this entry sends. */
  readonly depotId: string;
  readonly depotCode: string;
  readonly label: string;
  /** A `DEPOT` entry whose depot is a Value Centre: the choice screen lists its accounts under
   *  "Select your VC depot". `false` for every other entry, and when an older server omits it. */
  readonly isValueCentre: boolean;
  /** Enabler E8-G (ADO-3109), task 2.6: a Value Centre SL1.0 shows as a tile of its own (4 Aces,
   *  Better Deal, Corrib Foods, Collooney), listed with the depots rather than under "Select your
   *  VC depot". It stays a Value Centre (`isValueCentre`) for every other rule. Absent reads as
   *  `false`. */
  readonly showsAsOwnTile?: boolean;
  /** Empty for staff (`DEPOT_IDS`), lowest code first. Story 8.4 (ADO-2013): a Value Centre's
   *  `DEPOT` entry offers every `DEPOT` account the Customer holds there, and the Shopper picks
   *  one (its own cart, send and reads); every other entry offers one, the Customer's lowest-code
   *  account at the depot (the Ambient one at WOW). */
  readonly accounts: readonly OrderingAccount[];
  /** How many live accounts the Customer holds where that account is. Absent from an older
   *  server. Story 8.4 (ADO-2013): no display rule reads it any more, as a Value Centre names
   *  its account however many the Customer holds there (`namesItsAccount`). */
  readonly heldAccountCount?: number;
  /** Enabler E8-G (ADO-3109): the range the entry's carts order from (`CHILL`, `CHILLFRUITVEG`,
   *  `VC_61`…), which the order history read takes to show that range's orders on their own.
   *  Absent when the server names none (no range, a failed read, or an older server). */
  readonly assortmentCode?: string;
}

/** One per cart (depot, Trading Account and entry kind) holding a line. */
export interface OpenCart {
  /** Enabler E8-G (ADO-3109), DA-1: the cart and the revision it was listed at. The /choose
   *  "Submit now" has no cart view of its own, so its send carries this revision: a cart that
   *  changed since it was listed is refused rather than sent as it is now. The server always
   *  sends both; each is absent here only when it sent a malformed one. */
  readonly cartId?: string;
  readonly revision?: number;
  readonly depotId: string;
  /** Story 8.4 (ADO-2013): the Trading Account the cart is keyed by, so each Value Centre
   *  account has an open cart of its own. Enabler E8-G (ADO-3109), DA-10: the server always
   *  names one, a staff user's carts included; `null` only when it sent none or a malformed
   *  one. */
  readonly customerAccountCode: string | null;
  /** Enabler E8-G (ADO-3109), DA-8 (AC9): the kind of the entry the cart belongs to, so Chill's
   *  and Fruit & Veg's carts at depot 25 are listed apart. A missing or unknown kind (an older
   *  server) reads as `DEPOT`, the server's own default. */
  readonly entryKind: OrderingEntryKind;
  readonly caseCount: number;
  readonly lineCount: number;
  /** The cart's value, its live lines' locked line totals summed by
   *  the server. Absent when unknown: the server sent `null` (lines in mixed currencies, or a
   *  stored total it could not read), a malformed value, or none (an older server). */
  readonly value?: MoneyValue;
}

export interface OrderingChoices {
  readonly scope: OrderingScope;
  readonly linked: boolean;
  /** The Customer's central billing code when linked, else null. */
  readonly customerCode: string | null;
  readonly entries: readonly OrderingEntry[];
  /** `[]` both when none are open and when the read failed: see `openCartsAvailable`. */
  readonly openCarts: readonly OpenCart[];
  /** `false` when the server could not read the open carts (so `[]` is "unknown", not
   *  "none open"). The choices themselves are still valid. */
  readonly openCartsAvailable: boolean;
}

const SCOPES: readonly OrderingScope[] = ['TRADING_ACCOUNTS', 'DEPOT_IDS'];
const KINDS: readonly OrderingEntryKind[] = [
  'DEPOT',
  'WOW',
  'FRUIT_AND_VEG',
  'CORRIB_RETAIL',
  'LSN',
];
const ACCOUNT_TYPES: readonly TradingAccountType[] = [
  'LSN',
  'LICENSED_TRADE',
  'FOODSERVICE',
  'DEFAULT',
];

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object';
}

function nonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value !== '';
}

function oneOf<T extends string>(allowed: readonly T[], value: unknown): value is T {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value);
}

function parseAccount(value: unknown): OrderingAccount | null {
  if (!isRecord(value)) return null;
  const { customerAccountCode, accountName, tradingAccountType, marginShown } = value;
  if (!nonEmptyString(customerAccountCode)) return null;
  if (!oneOf(ACCOUNT_TYPES, tradingAccountType)) return null;
  return {
    customerAccountCode,
    accountName: typeof accountName === 'string' ? accountName : '',
    tradingAccountType,
    marginShown: marginShown === true,
  };
}

function parseEntry(value: unknown): OrderingEntry | null {
  if (!isRecord(value)) return null;
  const {
    key,
    kind,
    depotId,
    depotCode,
    label,
    isValueCentre,
    showsAsOwnTile,
    accounts,
    heldAccountCount,
    assortmentCode,
  } = value;
  if (!nonEmptyString(key) || !oneOf(KINDS, kind) || !nonEmptyString(depotId)) return null;
  if (!nonEmptyString(label)) return null;
  return {
    key,
    kind,
    depotId,
    depotCode: typeof depotCode === 'string' ? depotCode : '',
    label,
    isValueCentre: isValueCentre === true,
    showsAsOwnTile: showsAsOwnTile === true,
    accounts: Array.isArray(accounts)
      ? accounts.map(parseAccount).filter((account): account is OrderingAccount => account !== null)
      : [],
    ...(typeof heldAccountCount === 'number' ? { heldAccountCount } : {}),
    ...(nonEmptyString(assortmentCode) ? { assortmentCode } : {}),
  };
}

function parseOpenCart(value: unknown): OpenCart | null {
  if (!isRecord(value)) return null;
  const {
    cartId,
    revision,
    depotId,
    customerAccountCode,
    entryKind,
    caseCount,
    lineCount,
    value: money,
  } = value;
  if (!nonEmptyString(depotId)) return null;
  if (typeof caseCount !== 'number' || typeof lineCount !== 'number') return null;
  // The value is a display hint like `heldAccountCount`: a bad one is left out, never the cart.
  // So are a bad cart id and revision: the cart still lists, and its "Submit now" then sends the
  // revision of the basket on screen.
  return {
    ...(nonEmptyString(cartId) ? { cartId } : {}),
    ...(Number.isInteger(revision) && (revision as number) >= 0
      ? { revision: revision as number }
      : {}),
    depotId,
    customerAccountCode: nonEmptyString(customerAccountCode) ? customerAccountCode : null,
    entryKind: oneOf(KINDS, entryKind) ? entryKind : 'DEPOT',
    caseCount,
    lineCount,
    ...(isMoneyValue(money) ? { value: money } : {}),
  };
}

/** Read the caller's ordering choices. Throws on a non-200, a network error or a bad body. */
export async function fetchOrderingChoices(): Promise<OrderingChoices> {
  const res = await apiFetch(ORDERING_CHOICES_PATH);
  if (!res.ok) throw new Error(`Failed to load ordering choices (${res.status})`);
  const body = (await res.json()) as unknown;
  if (!isRecord(body) || !oneOf(SCOPES, body.scope)) {
    throw new Error('Malformed ordering choices response');
  }
  const seenKeys = new Set<string>();
  const entries = (Array.isArray(body.entries) ? body.entries : [])
    .map(parseEntry)
    .filter((entry): entry is OrderingEntry => {
      if (entry === null || seenKeys.has(entry.key)) return false;
      seenKeys.add(entry.key);
      return true;
    });
  return {
    scope: body.scope,
    linked: body.linked === true,
    customerCode: nonEmptyString(body.customerCode) ? body.customerCode : null,
    entries,
    openCarts: (Array.isArray(body.openCarts) ? body.openCarts : [])
      .map(parseOpenCart)
      .filter((cart): cart is OpenCart => cart !== null),
    openCartsAvailable: body.openCartsAvailable !== false,
  };
}

// Enabler E8-G (ADO-3109), DA-10 (AC11): the store accounts a staff user may order for.
//
// `GET /api/v1/ordering/accounts?depotId=<uuid>&q=<text>` (order-capture
// `OrderingStaffAccountDto`, hand-duplicated here; keep the two in lock-step) returns a bare
// array, at most 50, ordered by depot then account code; a blank `q` returns `[]`. A staff user
// has no account of its own, so it picks one here and then names it on every cart call for that
// depot, as a store user does. Like the choices read this one THROWS on failure, so "no account
// found" (`[]`) and "the search failed" stay apart. Malformed rows are dropped.

export const ORDERING_STAFF_ACCOUNTS_PATH = `${API_PREFIX}/ordering/accounts`;

/** One store account a staff user may order for. */
export interface OrderingStaffAccount {
  readonly depotCode: string;
  /** The "Account Number" the cart calls name. */
  readonly customerAccountCode: string;
  /** The account's name; '' when blank. */
  readonly accountName: string;
  /** The account's type, so a staff user sees what the store's own users see. */
  readonly tradingAccountType: TradingAccountType;
  /** Story 11.10 (ADO-1961), D-3: as `OrderingAccount.marginShown`; absent or malformed is
   *  `false`. */
  readonly marginShown: boolean;
}

/** A staff search row, or `null` when it has no account code or an account type the web does
 *  not know (refused, as an entry's account is). */
function parseStaffAccount(value: unknown): OrderingStaffAccount | null {
  if (!isRecord(value)) return null;
  const { depotCode, customerAccountCode, accountName, tradingAccountType, marginShown } = value;
  if (!nonEmptyString(customerAccountCode)) return null;
  if (!oneOf(ACCOUNT_TYPES, tradingAccountType)) return null;
  return {
    depotCode: typeof depotCode === 'string' ? depotCode : '',
    customerAccountCode,
    accountName: typeof accountName === 'string' ? accountName : '',
    tradingAccountType,
    marginShown: marginShown === true,
  };
}

/** Search the store accounts at `depotId` matching `query`. A blank query asks nothing and
 *  finds nothing. Throws on a non-200, a network error or a body that is not an array. */
export async function searchStaffAccounts(
  depotId: string,
  query: string,
  options: { readonly signal?: AbortSignal } = {},
): Promise<readonly OrderingStaffAccount[]> {
  const q = query.trim();
  if (q === '') return [];
  const params = new URLSearchParams({ depotId, q });
  const res = await apiFetch(
    `${ORDERING_STAFF_ACCOUNTS_PATH}?${params.toString()}`,
    options.signal === undefined ? {} : { signal: options.signal },
  );
  if (!res.ok) throw new Error(`Failed to search store accounts (${res.status})`);
  const body = (await res.json()) as unknown;
  if (!Array.isArray(body)) throw new Error('Malformed store accounts response');
  return body
    .map(parseStaffAccount)
    .filter((account): account is OrderingStaffAccount => account !== null);
}
